import fc from 'fast-check';
import reactUpdate from 'immutability-helper';

import {MinimalFolder, NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreRegistry, RegistrySummary} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  RegistrySummaryQuery,
  RegistrySummaryQueryVariables,
} from 'registry/graphql/__generated/RegistrySummaryQuery';
import {REGISTRY_SUMMARY_QUERY} from 'registry/graphql/RegistrySummary';
import {ORGANIZATION} from 'test/fixtures/parties/OrganizationFixtures';
import {SOURCE_USER_SUMMARY, SOURCE_USER_SUMMARY_GQL, USER} from 'test/fixtures/parties/UserFixtures';
import {
  DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_REST,
  DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL,
} from 'test/fixtures/PermissionFixtures';
import {WRITE_POLICY} from 'test/fixtures/PolicyFixtures';
import {makeTypesafeQueryMock, TypesafeMockedResponse} from 'test/lib/MockGraphQLProvider';

import {NavStoreRegistryGraphQL} from '../../../../graphql-fragments/nav/__generated/NavStoreRegistryGraphQL';
import {FolderSummaryGraphQL} from '../../../../graphql-fragments/nav/folders/__generated/FolderSummaryGraphQL';
import {RegistryGraphQL} from '../../../../graphql-fragments/registry/__generated/RegistryGraphQL';
import {RegistrySummaryGraphQL} from '../../../../graphql-fragments/registry/__generated/RegistrySummaryGraphQL';

export const LabelingStrategyArbitrary = fc.oneof(
  ...Object.keys(RegistryLabelingStrategy).map((key: keyof typeof RegistryLabelingStrategy) =>
    fc.constant(RegistryLabelingStrategy[key])
  )
);

export const REGISTRY_SUMMARY: RegistrySummary = {
  archived: false,
  archiveRecord: null,
  areSettingsAdminOnly: false,
  createdAt: '2016-11-08T19:05:48.402226+00:00',
  creator: SOURCE_USER_SUMMARY,
  disablePersonalCollections: true,
  useStrictValidation: true,
  hidden: false,
  id: 'src_IIHi0wVy',
  locked: false,
  modifiedAt: '2016-11-08T19:06:30.258610+00:00',
  name: 'Enterprise Registry',
  ownerInfo: {
    memberDataPolicyId: WRITE_POLICY.id,
    type: 'organization',
  },
  public: false,
  alwaysShowAncestorFolderNames: true,
  rootFolder: {
    archiveRecord: null,
    api_identifier: 'lib_Tsur0TI6',
    created_at: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    id: 23,
    dbId: 23,
    name: 'Enterprise Registry',
    owner: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'testOrg',
      id: 'ent_6AF4sRH3',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.247326+00:00',
      name: 'testOrg',
      type: ModelTypeConstants.ORGANIZATION,
      url_identifier: 'testorg',
    },
    owner_handle: 'testOrg',
    owner_url: '/testorg/',
    owner_url_identifier: 'testorg',
    parent: null,
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
  s_owner: ORGANIZATION,
  sourceType: 'registry',
  starred: false,
  url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
};

// This returns Source.to_dict_with_perms(include_collaborators=True) for a registry.
export const REGISTRY: NavStoreRegistry = {
  ...REGISTRY_SUMMARY,
  collaborators: [USER],
  description: null,
  permissions: DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_REST,
};

export const FOLDERS: Array<NumberIDFolder> = [
  {
    api_identifier: 'lib_LL2mWCBC',
    created_at: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    id: 22,
    dbId: 22,
    name: 'Enterprise Registry',
    owner: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'benchling',
      id: 'ent_9vbEqh2J',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.189484+00:00',
      name: 'Benchling Lab',
      type: ModelTypeConstants.ORGANIZATION,
      url_identifier: 'benchling',
    },
    owner_handle: 'benchling',
    owner_url: '/benchling/',
    owner_url_identifier: 'benchling',
    parent: null,
    source: REGISTRY,
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
];

export const REGISTRY_ADMIN = reactUpdate(REGISTRY, {
  permissions: {admin: {$set: true}, locationsCreatable: {$set: true}},
});

export const REGISTRY_ROOT: StringIDFolder = {
  ...REGISTRY.rootFolder,
  id: REGISTRY.rootFolder.api_identifier,
  source: FOLDERS[0].source,
};

export const MINIMAL_REGISTRY_ROOT: MinimalFolder = {
  archiveRecord: null,
  api_identifier: 'lib_Tsur0TI6',
  id: 'lib_Tsur0TI6',
  name: 'Enterprise Registry',
  owner_handle: 'testOrg',
  parent: null,
  type: 'ALL',
  source: REGISTRY,
};

export const REGISTRY_WITH_ORGANIZATION_GQL: RegistryGraphQL = {
  __typename: 'RegistryNode',
  id: REGISTRY_SUMMARY.id,
  organization: {
    __typename: 'OrganizationNode',
    id: ORGANIZATION.id,
  },
};

export const REGISTRY_SUMMARY_GQL: RegistrySummaryGraphQL = {
  __typename: 'RegistryNode',
  archived: false,
  archiveRecord: null,
  areSettingsAdminOnly: false,
  createdAt: '2016-11-08T19:05:48.402226+00:00',
  creator: SOURCE_USER_SUMMARY_GQL,
  disablePersonalCollections: true,
  useStrictValidation: true,
  hidden: false,
  id: 'src_IIHi0wVy',
  locked: false,
  modifiedAt: '2016-11-08T19:06:30.258610+00:00',
  name: 'Enterprise Registry',
  ownerInfoJson: {
    memberDataPolicyId: WRITE_POLICY.id,
    type: 'organization',
  },
  public: false,
  alwaysShowAncestorFolderNames: true,
  rootFolder: {
    __typename: 'FolderNode',
    archiveRecord: null,
    id: 'lib_Tsur0TI6',
    createdAt: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    dbId: 23,
    name: 'Enterprise Registry',
    ownerJson: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'testOrg',
      id: 'ent_6AF4sRH3',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.247326+00:00',
      name: 'testOrg',
      type: 'organization',
      url_identifier: 'testorg',
    },
    ownerUrl: '/testorg/',
    parentFolders: [],
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
  ownerJson: ORGANIZATION,
  sourceType: 'registry',
  starred: false,
  url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
};

export const REGISTRY_SUMMARY_GQL_USE_STRICT_VALIDATION_FALSE: RegistrySummaryGraphQL = {
  ...REGISTRY_SUMMARY_GQL,
  useStrictValidation: false,
};

export const REGISTRY_GQL: NavStoreRegistryGraphQL = {
  ...REGISTRY_SUMMARY_GQL,
  collaboratorsJson: [USER],
  description: null,
  permissions: {
    ...DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL,
    admin: false,
    locationsCreatable: false,
    canManageCollaborators: false,
  },
};

export const REGISTRY_WITH_ONLY_ID_GQL = {
  __typename: 'RegistryNode' as const,
  id: REGISTRY.id,
};

export const REGISTRY_ADMIN_GQL = reactUpdate(REGISTRY_GQL, {
  permissions: {admin: {$set: true}, locationsCreatable: {$set: true}},
});

export function makeRegistrySummaryMock(registryId: string): TypesafeMockedResponse {
  return makeTypesafeQueryMock<RegistrySummaryQuery, RegistrySummaryQueryVariables>({
    query: REGISTRY_SUMMARY_QUERY,
    variables: {registryId},
    data: {
      registry: REGISTRY_SUMMARY_GQL,
    },
  });
}

export const REGISTRY_ROOT_GQL: FolderSummaryGraphQL = {
  ...REGISTRY_GQL.rootFolder,
  source: REGISTRY_SUMMARY_GQL,
  parentFolders: [],
};
